import _ from 'lodash';
import { DateTime } from 'luxon';
import moment from 'moment';

import type { RankVersion } from '@appTypes/common/versions';

import { versionStatusLabels } from '../../../advancement/constants';
import type { BulkEntrySupportedVersion } from '../types';

/**
 * Checks whether a version is: current, expired, or upcoming
 *
 * Used in:
 * - Version selector dropdown for Bulk Entry
 */
export function getVersionStatusLabel(version: BulkEntrySupportedVersion) {
  const now = moment();

  const effectiveDt = moment(version.versionEffectiveDt);
  const expiryDt = moment(version.versionExpiryDt);
  const expiredDt = moment(
    (version as RankVersion).expiredDate || version.versionExpiryDt,
  );
  const isExpired = expiredDt.isBefore(now) || expiryDt.isBefore(now);
  const isUpcoming = effectiveDt.isAfter(now);

  if (isExpired)
    return {
      status: versionStatusLabels.EXPIRED,
      date: expiredDt,
    };
  else if (isUpcoming) {
    return {
      status: versionStatusLabels.UPCOMING,
      date: effectiveDt,
    };
  } else {
    // current version
    return {
      status: versionStatusLabels.CURRENT,
      date: expiryDt,
    };
  }
}

/**
 * Filters advancement versions and returns only 2 which are either:
 * - `[current, future]` OR `[past (if not expired), current]`
 *
 */
export function filterVersions(
  versions: BulkEntrySupportedVersion[],
): BulkEntrySupportedVersion[] {
  const today = Date.now();

  if (!versions || _.isEmpty(versions)) return [];

  const activeVersion = versions.find(
    version =>
      version.versionEffectiveDt &&
      !version.versionExpiryDt &&
      moment(version.versionEffectiveDt).isBefore(today),
  );
  const otherVersions = _.chain(versions)
    .filter(version => {
      const lacksExpiredDate =
        _.isNil(version.expiredDate) || _.isEmpty(version.expiredDate);
      const expiredDate = DateTime.fromISO(
        (version.expiredDate as string) ?? '',
      );
      const now = DateTime.now();
      return (
        version !== activeVersion && (lacksExpiredDate || expiredDate > now)
      );
    })
    .orderBy('versionEffectiveDt', 'desc')
    .take(1)
    .value();

  const result = _.chain(
    // in case `activeVersion` is not found, remove undefined from array
    [activeVersion].filter(Boolean) as BulkEntrySupportedVersion[],
  )
    .concat(otherVersions)
    .orderBy(
      ['versionEffectiveDt', 'expiredDate', 'versionExpiryDt'],
      ['desc', 'desc', 'desc'],
    )
    .take(2)
    .value();

  return result;
}

/**
 * Filters merit badge versions and returns only those within 10 years
 *
 */
export function filterMeritBadgeVersions(
  versions: BulkEntrySupportedVersion[],
): BulkEntrySupportedVersion[] {
  const today = DateTime.now();
  const tenYearsAgo = today.minus({ years: 10 });

  if (!versions || _.isEmpty(versions)) return [];

  const activeVersion = versions.find(
    version =>
      version.versionEffectiveDt &&
      !version.versionExpiryDt &&
      DateTime.fromISO(version.versionEffectiveDt as string) < today,
  );

  const otherVersions = _.chain(versions)
    .filter(version => {
      if (version === activeVersion) return false;

      const expiry = DateTime.fromISO(version.versionExpiryDt as string);
      // Not expired version
      if (!expiry.isValid) return true;

      // Keep only if expiry is within last 10 years
      return expiry >= tenYearsAgo && expiry <= today;
    })
    .orderBy('versionEffectiveDt', 'desc')
    .value();

  const result = _.chain(
    // in case `activeVersion` is not found, remove undefined from array
    [activeVersion].filter(Boolean) as BulkEntrySupportedVersion[],
  )
    .concat(otherVersions)
    .orderBy(
      ['versionEffectiveDt', 'expiredDate', 'versionExpiryDt'],
      ['desc', 'desc', 'desc'],
    )
    .value();

  return result;
}

interface DatedVersion {
  versionEffectiveDt: string;
  versionExpiryDt?: string | null;
  expiredDate?: string | null;
}

/**
 * Latest version currently in effect, falling back to the latest by effective date
 */
export function getLatestActiveVersion<T extends DatedVersion>(
  versions: T[],
): T | undefined {
  const now = DateTime.now();
  const active = versions.filter(v => {
    const effective = DateTime.fromISO(v.versionEffectiveDt);
    const expiry = DateTime.fromISO(v.versionExpiryDt || v.expiredDate || '');
    return effective <= now && (!expiry.isValid || expiry >= now);
  });

  if (active.length > 0) {
    return active.reduce((latest, v) =>
      DateTime.fromISO(v.versionEffectiveDt) >
      DateTime.fromISO(latest.versionEffectiveDt)
        ? v
        : latest,
    );
  }

  // maxBy skips NaN, so versions with unparseable dates fall back to the first one
  return (
    _.maxBy(versions, v => DateTime.fromISO(v.versionEffectiveDt).toMillis()) ??
    versions[0]
  );
}
